import React, { useState, useEffect } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import { 
  Plus, Edit2, Trash2, Package, DollarSign, Clock, Tag, Users,
  Search, Filter, X, Save, CheckCircle, AlertCircle, Target, Zap,
  Play, FileText, TrendingUp, ChevronRight, ExternalLink, Link2, Eye,
  Code2, Share2, Headphones, Globe, ShoppingCart, Palette
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';

// Icon mapping for services
const iconMap = {
  Code2, Share2, Target, Headphones, Globe, ShoppingCart, Palette,
  Package, Zap, TrendingUp, Users, FileText
};

const getServiceIcon = (iconName, fallback) => {
  const IconComponent = iconMap[iconName];
  if (IconComponent) {
    return <IconComponent className="w-6 h-6" />;
  }
  return fallback || '?';
};

const Services = () => {
  const { settings } = useSettings();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [selectedService, setSelectedService] = useState(null); // For detail view
  const [activeDetailTab, setActiveDetailTab] = useState('info');
  
  const primaryColor = settings?.primaryColor || '#00b30c';
  const currency = settings?.currency || 'COP';

  // Form state with ALL fields including video, presentations, cases
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shortDescription: '',
    category: '',
    targetProfile: '',
    painPoints: '',
    minPrice: '',
    maxPrice: '',
    currency: 'COP',
    duration: '',
    durationUnit: 'months',
    status: 'active',
    scope: [],
    limitations: [],
    keyFeatures: [],
    // New fields for rich content
    videoUrl: '',
    presentations: [], // [{name, url, type}]
    successCases: [], // [{title, clientName, clientLogo, description, url}]
    icon: '',
    color: '#6366f1'
  });
  
  const [newScope, setNewScope] = useState('');
  const [newLimitation, setNewLimitation] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newPresentation, setNewPresentation] = useState({ name: '', url: '', type: 'pdf' });
  const [newCase, setNewCase] = useState({ title: '', clientName: '', description: '', url: '' });

  // Categories
  const categories = ['Tecnología', 'Dedicados', 'Agencia Digital'];

  // Load services
  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      const data = await api.getServices();
      setServices(data || []);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Error al cargar servicios');
    } finally {
      setLoading(false);
    }
  };

  // Filter services
  const filteredServices = services.filter(service => {
    const matchesSearch = !searchQuery || 
      service.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.targetProfile?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !categoryFilter || service.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Format currency
  const formatPrice = (value) => {
    if (!value) return '0';
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(value);
  };

  // Handle form
  const handleOpenModal = (service = null, e = null) => {
    if (e) e.stopPropagation();
    
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name || '',
        description: service.description || '',
        shortDescription: service.shortDescription || '',
        category: service.category || '',
        targetProfile: service.targetProfile || '',
        painPoints: service.painPoints || '',
        minPrice: service.minPrice || '',
        maxPrice: service.maxPrice || '',
        currency: service.currency || 'COP',
        duration: service.duration || '',
        durationUnit: service.durationUnit || 'months',
        status: service.status || 'active',
        scope: service.scope || [],
        limitations: service.limitations || [],
        keyFeatures: service.keyFeatures || [],
        videoUrl: service.videoUrl || '',
        presentations: service.presentations || [],
        successCases: service.successCases || [],
        icon: service.icon || '',
        color: service.color || '#6366f1'
      });
    } else {
      setEditingService(null);
      setFormData({
        name: '',
        description: '',
        shortDescription: '',
        category: '',
        targetProfile: '',
        painPoints: '',
        minPrice: '',
        maxPrice: '',
        currency: 'COP',
        duration: '',
        durationUnit: 'months',
        status: 'active',
        scope: [],
        limitations: [],
        keyFeatures: [],
        videoUrl: '',
        presentations: [],
        successCases: [],
        icon: '',
        color: '#6366f1'
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      const dataToSave = {
        ...formData,
        minPrice: formData.minPrice ? Number(formData.minPrice) : 0,
        maxPrice: formData.maxPrice ? Number(formData.maxPrice) : 0,
        // Convert empty string to null for duration (backend expects float or null)
        duration: formData.duration === '' || formData.duration === null ? null : Number(formData.duration)
      };
      
      if (editingService) {
        await api.updateService(editingService.id, dataToSave);
        toast.success('Servicio actualizado');
      } else {
        await api.createService(dataToSave);
        toast.success('Servicio creado');
      }
      setShowModal(false);
      loadServices();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Error al guardar servicio');
    }
  };

  const handleDelete = async (serviceId) => {
    try {
      await api.deleteService(serviceId);
      toast.success('Servicio eliminado');
      setShowDeleteConfirm(null);
      loadServices();
    } catch (error) {
      toast.error('Error al eliminar servicio');
    }
  };

  // Array helpers
  const addToArray = (field, value, setValue) => {
    if (typeof value === 'string' ? value.trim() : Object.values(value).some(v => v)) {
      setFormData(prev => ({ ...prev, [field]: [...prev[field], typeof value === 'string' ? value.trim() : value] }));
      setValue(typeof value === 'string' ? '' : { name: '', url: '', type: 'pdf' });
    }
  };

  const removeFromArray = (field, index) => {
    setFormData(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  // View service detail
  const handleViewService = (service) => {
    setSelectedService(service);
    setActiveDetailTab('info');
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: primaryColor }}></div>
      </div>
    );
  }

  // Service Detail View
  if (selectedService) {
    return (
      <div className="p-6 space-y-6">
        {/* Back button */}
        <button 
          onClick={() => setSelectedService(null)} 
          className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Volver a servicios
        </button>

        {/* Service Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div 
                className="w-16 h-16 rounded-xl flex items-center justify-center text-white text-2xl font-bold bg-white/20"
              >
                {getServiceIcon(selectedService.icon, selectedService.name?.charAt(0))}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-2xl font-bold">{selectedService.name}</h2>
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    selectedService.status === 'active' 
                      ? 'bg-green-400/20 text-green-100' 
                      : 'bg-white/20 text-white/80'
                  }`}>
                    {selectedService.status === 'active' ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <p className="text-purple-100">{selectedService.category}</p>
                {selectedService.minPrice && (
                  <p className="text-lg font-semibold mt-2">
                    Desde {formatPrice(selectedService.minPrice)}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={(e) => handleOpenModal(selectedService, e)}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Editar
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
          {[
            { id: 'info', label: 'Información' },
            { id: 'video', label: 'Video' },
            { id: 'presentations', label: 'Presentaciones' },
            { id: 'cases', label: 'Casos de Éxito' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveDetailTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeDetailTab === tab.id 
                  ? 'border-purple-600 text-purple-600' 
                  : 'border-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          {activeDetailTab === 'info' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Descripción</h3>
                <p className="text-slate-600 whitespace-pre-wrap">{selectedService.description || 'Sin descripción'}</p>
              </div>
              
              {selectedService.targetProfile && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Perfil Objetivo</h3>
                  <p className="text-slate-600">{selectedService.targetProfile}</p>
                </div>
              )}

              {selectedService.painPoints && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Dolor que Soluciona</h3>
                  <p className="text-slate-600">{selectedService.painPoints}</p>
                </div>
              )}
              
              {selectedService.keyFeatures?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Características Clave</h3>
                  <ul className="space-y-2">
                    {selectedService.keyFeatures.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {selectedService.scope?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Alcance</h3>
                  <ul className="space-y-1">
                    {selectedService.scope.map((item, idx) => (
                      <li key={idx} className="text-slate-600 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedService.limitations?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Limitaciones</h3>
                  <ul className="space-y-1">
                    {selectedService.limitations.map((item, idx) => (
                      <li key={idx} className="text-slate-600 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {activeDetailTab === 'video' && (
            <div>
              {selectedService.videoUrl ? (
                <div className="aspect-video rounded-xl overflow-hidden bg-slate-900">
                  <iframe
                    src={selectedService.videoUrl.replace('watch?v=', 'embed/')}
                    title={selectedService.name}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <Play className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No hay video disponible para este servicio</p>
                  <button 
                    onClick={(e) => handleOpenModal(selectedService, e)}
                    className="mt-4 text-purple-600 hover:underline text-sm font-medium"
                  >
                    Agregar video
                  </button>
                </div>
              )}
            </div>
          )}

          {activeDetailTab === 'presentations' && (
            <div>
              {selectedService.presentations?.length > 0 ? (
                <div className="grid gap-4">
                  {selectedService.presentations.map((pres, idx) => (
                    <a 
                      key={idx}
                      href={pres.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{pres.name}</p>
                        <p className="text-sm text-slate-500">{pres.type?.toUpperCase() || 'Documento'}</p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-slate-400" />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No hay presentaciones disponibles</p>
                  <button 
                    onClick={(e) => handleOpenModal(selectedService, e)}
                    className="mt-4 text-purple-600 hover:underline text-sm font-medium"
                  >
                    Agregar presentación
                  </button>
                </div>
              )}
            </div>
          )}

          {activeDetailTab === 'cases' && (
            <div>
              {selectedService.successCases?.length > 0 ? (
                <div className="grid gap-4">
                  {selectedService.successCases.map((caseItem, idx) => (
                    <div key={idx} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900">{caseItem.title}</h4>
                          {caseItem.clientName && (
                            <p className="text-sm text-purple-600">{caseItem.clientName}</p>
                          )}
                          <p className="text-slate-600 mt-2">{caseItem.description}</p>
                          {caseItem.url && (
                            <a 
                              href={caseItem.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm text-purple-600 hover:underline mt-2"
                            >
                              Ver más <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No hay casos de éxito disponibles</p>
                  <button 
                    onClick={(e) => handleOpenModal(selectedService, e)}
                    className="mt-4 text-purple-600 hover:underline text-sm font-medium"
                  >
                    Agregar caso de éxito
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Servicios</h1>
          <p className="text-slate-500 mt-1">Gestiona tu catálogo de servicios</p>
        </div>
        
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-all"
          style={{ backgroundColor: primaryColor }}
        >
          <Plus className="w-5 h-5" />
          Nuevo Servicio
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar servicios..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 bg-white"
        >
          <option value="">Todas las categorías</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Services Grid - Compact Cards */}
      {filteredServices.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            {searchQuery || categoryFilter ? 'No se encontraron servicios' : 'No hay servicios creados'}
          </h3>
          <p className="text-slate-500 mb-4">
            {searchQuery || categoryFilter 
              ? 'Intenta con otros filtros de búsqueda'
              : 'Crea tu primer servicio para comenzar'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredServices.map(service => (
            <div 
              key={service.id} 
              onClick={() => handleViewService(service)}
              className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-lg hover:border-purple-200 transition-all cursor-pointer group"
            >
              <div className="flex items-start gap-3">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
                  style={{ backgroundColor: service.color || '#6366f1' }}
                >
                  {getServiceIcon(service.icon, service.name?.charAt(0))}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-slate-900 group-hover:text-purple-600 transition-colors truncate">
                      {service.name}
                    </h3>
                    <button
                      onClick={(e) => handleOpenModal(service, e)}
                      className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      title="Editar servicio"
                    >
                      <Edit2 className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>
                  <p className="text-sm text-slate-500 line-clamp-2 mt-1">
                    {service.shortDescription || service.description?.substring(0, 80)}
                  </p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-sm font-medium text-purple-600">
                      {service.minPrice ? `Desde ${formatPrice(service.minPrice)}` : 'Consultar precio'}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        service.status === 'active' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-100 text-slate-600'
                      }`}>
                        {service.status === 'active' ? 'Activo' : 'Inactivo'}
                      </span>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-purple-600 transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal - Full Featured */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-semibold text-slate-900">
                {editingService ? 'Editar Servicio' : 'Nuevo Servicio'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2 pb-2 border-b border-slate-100">
                  <Package className="w-4 h-4" /> Información Básica
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nombre del Servicio <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                      placeholder="Ej: Manejo de Redes Sociales"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Categoría</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 bg-white"
                    >
                      <option value="">Seleccionar</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Descripción Corta</label>
                    <input
                      type="text"
                      value={formData.shortDescription}
                      onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                      placeholder="Breve descripción para las tarjetas"
                      maxLength={100}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Descripción Completa</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Icono (emoji o letra)</label>
                    <input
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                      placeholder="M"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="w-12 h-10 border border-slate-200 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.color}
                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 bg-white"
                    >
                      <option value="active">Activo</option>
                      <option value="inactive">Inactivo</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Target & Pain */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2 pb-2 border-b border-slate-100">
                  <Users className="w-4 h-4" /> Cliente y Problema
                </h4>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Perfil de Cliente Objetivo</label>
                  <input
                    type="text"
                    value={formData.targetProfile}
                    onChange={(e) => setFormData({ ...formData, targetProfile: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="Ej: Empresas B2B y B2C que buscan fortalecer su presencia digital"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Dolor que Soluciona</label>
                  <textarea
                    value={formData.painPoints}
                    onChange={(e) => setFormData({ ...formData, painPoints: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 resize-none"
                    placeholder="Ej: Falta de presencia digital, bajo engagement en redes"
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2 pb-2 border-b border-slate-100">
                  <DollarSign className="w-4 h-4" /> Precios
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Precio Mínimo (COP)</label>
                    <input
                      type="number"
                      value={formData.minPrice}
                      onChange={(e) => setFormData({ ...formData, minPrice: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Precio Máximo (COP)</label>
                    <input
                      type="number"
                      value={formData.maxPrice}
                      onChange={(e) => setFormData({ ...formData, maxPrice: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    />
                  </div>
                </div>
              </div>

              {/* Video URL */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2 pb-2 border-b border-slate-100">
                  <Play className="w-4 h-4" /> Video
                </h4>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">URL del Video (YouTube)</label>
                  <input
                    type="url"
                    value={formData.videoUrl}
                    onChange={(e) => setFormData({ ...formData, videoUrl: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                </div>
              </div>

              {/* Key Features */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2 pb-2 border-b border-slate-100">
                  <Zap className="w-4 h-4" /> Características Clave
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('keyFeatures', newFeature, setNewFeature))}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="Ej: Reportes mensuales de métricas"
                  />
                  <button 
                    onClick={() => addToArray('keyFeatures', newFeature, setNewFeature)} 
                    className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                  >
                    Agregar
                  </button>
                </div>
                {formData.keyFeatures.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.keyFeatures.map((item, index) => (
                      <span key={index} className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm">
                        {item}
                        <button onClick={() => removeFromArray('keyFeatures', index)} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Scope */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2 pb-2 border-b border-slate-100">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Alcances
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newScope}
                    onChange={(e) => setNewScope(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('scope', newScope, setNewScope))}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="Ej: Creación de contenido para 3 redes"
                  />
                  <button onClick={() => addToArray('scope', newScope, setNewScope)} className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200">
                    Agregar
                  </button>
                </div>
                {formData.scope.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.scope.map((item, index) => (
                      <span key={index} className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm">
                        {item}
                        <button onClick={() => removeFromArray('scope', index)} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Limitations */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2 pb-2 border-b border-slate-100">
                  <AlertCircle className="w-4 h-4 text-orange-500" /> Limitaciones
                </h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newLimitation}
                    onChange={(e) => setNewLimitation(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addToArray('limitations', newLimitation, setNewLimitation))}
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="Ej: No incluye pauta publicitaria"
                  />
                  <button onClick={() => addToArray('limitations', newLimitation, setNewLimitation)} className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200">
                    Agregar
                  </button>
                </div>
                {formData.limitations.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.limitations.map((item, index) => (
                      <span key={index} className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full text-sm">
                        {item}
                        <button onClick={() => removeFromArray('limitations', index)} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Presentations */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2 pb-2 border-b border-slate-100">
                  <FileText className="w-4 h-4" /> Presentaciones
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={newPresentation.name}
                    onChange={(e) => setNewPresentation({ ...newPresentation, name: e.target.value })}
                    className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="Nombre"
                  />
                  <input
                    type="url"
                    value={newPresentation.url}
                    onChange={(e) => setNewPresentation({ ...newPresentation, url: e.target.value })}
                    className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="URL del documento"
                  />
                  <div className="flex gap-2">
                    <select
                      value={newPresentation.type}
                      onChange={(e) => setNewPresentation({ ...newPresentation, type: e.target.value })}
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg bg-white"
                    >
                      <option value="pdf">PDF</option>
                      <option value="pptx">PowerPoint</option>
                      <option value="doc">Word</option>
                      <option value="link">Enlace</option>
                    </select>
                    <button 
                      onClick={() => {
                        if (newPresentation.name && newPresentation.url) {
                          setFormData(prev => ({ ...prev, presentations: [...prev.presentations, newPresentation] }));
                          setNewPresentation({ name: '', url: '', type: 'pdf' });
                        }
                      }} 
                      className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                    >
                      +
                    </button>
                  </div>
                </div>
                {formData.presentations.length > 0 && (
                  <div className="space-y-2">
                    {formData.presentations.map((pres, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium">{pres.name}</span>
                          <span className="text-xs text-slate-500">({pres.type})</span>
                        </div>
                        <button onClick={() => removeFromArray('presentations', index)} className="text-red-500 hover:text-red-700">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Success Cases */}
              <div className="space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2 pb-2 border-b border-slate-100">
                  <TrendingUp className="w-4 h-4" /> Casos de Éxito
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={newCase.title}
                    onChange={(e) => setNewCase({ ...newCase, title: e.target.value })}
                    className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="Título del caso"
                  />
                  <input
                    type="text"
                    value={newCase.clientName}
                    onChange={(e) => setNewCase({ ...newCase, clientName: e.target.value })}
                    className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="Nombre del cliente"
                  />
                  <div className="sm:col-span-2">
                    <textarea
                      value={newCase.description}
                      onChange={(e) => setNewCase({ ...newCase, description: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 resize-none"
                      placeholder="Descripción del caso de éxito"
                      rows={2}
                    />
                  </div>
                  <input
                    type="url"
                    value={newCase.url}
                    onChange={(e) => setNewCase({ ...newCase, url: e.target.value })}
                    className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                    placeholder="URL (opcional)"
                  />
                  <button 
                    onClick={() => {
                      if (newCase.title && newCase.description) {
                        setFormData(prev => ({ ...prev, successCases: [...prev.successCases, newCase] }));
                        setNewCase({ title: '', clientName: '', description: '', url: '' });
                      }
                    }} 
                    className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                  >
                    Agregar Caso
                  </button>
                </div>
                {formData.successCases.length > 0 && (
                  <div className="space-y-2">
                    {formData.successCases.map((caseItem, index) => (
                      <div key={index} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{caseItem.title}</p>
                          {caseItem.clientName && <p className="text-xs text-purple-600">{caseItem.clientName}</p>}
                          <p className="text-xs text-slate-500 mt-1 line-clamp-1">{caseItem.description}</p>
                        </div>
                        <button onClick={() => removeFromArray('successCases', index)} className="text-red-500 hover:text-red-700">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-all"
                style={{ backgroundColor: primaryColor }}
              >
                <Save className="w-4 h-4" />
                {editingService ? 'Actualizar' : 'Crear Servicio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">¿Eliminar servicio?</h3>
                <p className="text-sm text-slate-500">Esta acción no se puede deshacer</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
